/**
 * L'ÉCHANGE DE DEUX AFFECTATIONS, côté navigateur (26/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « En posant sur une autre personne, qu'il y ait un échange entre les
 *   postes. »                                              — RQ, 26/10/2026
 *
 * Ce module dit, PENDANT LE GLISSÉ, si déposer une pastille sur une autre est
 * permis — pour griser les cases refusées avant qu'on n'y aille, au lieu de
 * laisser déposer puis refuser. Le SERVEUR revérifie tout (`POST
 * ged/rh/affectations/echanger`) : ceci ne fait qu'éviter un geste voué au
 * refus.
 *
 * ── Deux implémentations d'une même règle, et ce qui les tient ensemble ────
 *
 * `refusEchange` est le pendant exact de `backend/src/rh-echange.ts`. Deux
 * copies d'une règle divergent au premier ajout — et le glissé, chemin le plus
 * rapide, finirait par autoriser ce que le serveur refuse, ou l'inverse.
 * `tests/front-echange-planning.test.js` joue les MÊMES cas sur les deux et
 * exige les mêmes messages : le jour où l'une bouge seule, il échoue.
 *
 * `verdictLocal` — « qui peut tenir ce poste » — vivait dans `Rh.tsx` et sert le
 * dialogue d'affectation et le glissé de la semaine. Il est ICI pour que
 * l'échange du mois utilise la MÊME fonction, jamais une troisième.
 *
 * Module PUR : aucun import d'exécution.
 */

/* ── « Qui peut tenir ce poste » ─────────────────────────────────────────── */

type Exigence = string | { code: string; niveauMin?: string | null };

/**
 * Le verdict d'habilitation d'une personne sur un poste, depuis ce qu'elle
 * DÉTIENT (`rhDetenues`, `ged-habilitation-personne`) — et non plus depuis
 * `settings.habilitations`, matrice en cours de retrait dont les axes sont des
 * codes de poste périmés depuis le renommage des paillasses.
 *
 * ⚠️ La forme CHAÎNE reste lue, parce que rien ne garantit qu'aucun poste n'en
 * porte plus jamais : une exigence qu'on ne sait pas évaluer est déclarée
 * `indetermine` et laissée au serveur, jamais comptée comme satisfaite.
 */
export function verdictLocal(
  personne: { orgId: string },
  poste: { habilitationsRequises?: Exigence[] | null },
  detenuesParPersonne: Map<string, Map<string, string>>,
): { manquantes: string[]; enFormation: string[]; indetermine: string[]; ok: boolean } {
  const indetermine: string[] = [];
  const exigees = poste.habilitationsRequises || [];
  if (!exigees.length) return { manquantes: [], enFormation: [], indetermine: [], ok: true };
  const siennes = detenuesParPersonne.get(personne.orgId) || new Map<string, string>();
  const manquantes: string[] = [];
  const enFormation: string[] = [];
  for (const e of exigees) {
    // L'ancien modèle — une exigence qui nomme une discipline — n'est plus
    // évaluable depuis le retrait de la matrice. On ne la compte NI comme
    // satisfaite ni comme manquante : le serveur tranche, et on le dit.
    if (typeof e === 'string') { indetermine.push(e); continue; }
    const niveau = siennes.get(e.code);
    if (niveau === undefined) { manquantes.push(e.code); continue; }
    // « En formation » n'est JAMAIS une habilitation : c'est l'état de qui
    // apprend encore et ne doit pas tenir le poste seul.
    if (niveau === 'formation') enFormation.push(e.code);
  }
  // `indetermine` ne bloque PAS l'aperçu : il signale que seul le serveur sait.
  return { manquantes, enFormation, indetermine, ok: !manquantes.length && !enFormation.length };
}

/* ── L'échange ───────────────────────────────────────────────────────────── */

export interface CaseEchange {
  documentId: string;
  date: string;
  posteCode: string;
  orgId: string;
  nature?: string | null;
  derogation?: unknown;
  derogationAbsence?: unknown;
}

export interface LigneDuJour { documentId: string; posteCode: string; orgId: string }

const present = (v: unknown): boolean => {
  if (v === null || v === undefined || v === '') return false;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return true;
};

/** Pendant exact de `refusEchange` du serveur — mêmes cas, mêmes messages. */
export function refusEchange(a: CaseEchange, b: CaseEchange, jour: LigneDuJour[]): string | null {
  if (a.documentId === b.documentId) return 'Une affectation ne s’échange pas avec elle-même.';
  if (a.date !== b.date) {
    return 'L’échange se fait le même jour : ces deux affectations ne tombent pas à la même date.';
  }
  if (a.orgId === b.orgId) return 'C’est la même personne : il n’y a rien à échanger.';
  if (a.posteCode === b.posteCode) return 'Les deux affectations sont sur le même poste : il n’y a rien à échanger.';
  for (const x of [a, b]) {
    if ((x.nature || 'travail') !== 'travail') {
      return 'Une journée en formation ou en astreinte ne s’échange pas ici : le tuteur et la nature suivent la personne, pas la case. Passez par la case elle-même.';
    }
    if (present(x.derogation) || present(x.derogationAbsence)) {
      return 'Une affectation qui porte une dérogation ne s’échange pas : la dérogation vaut pour une personne sur un poste, elle ne se transmet pas.';
    }
  }
  const dejaLa = (qui: CaseEchange, poste: CaseEchange) => (jour || []).some(
    (l) => l.orgId === qui.orgId && l.posteCode === poste.posteCode && l.documentId !== poste.documentId,
  );
  if (dejaLa(a, b) || dejaLa(b, a)) {
    return 'L’une des deux personnes tient déjà, ce jour-là, le poste qu’elle prendrait : l’échange créerait un doublon.';
  }
  return null;
}

export interface PosteEchange {
  libelle?: string;
  actif?: boolean;
  habilitationsRequises?: Exigence[] | null;
}

/**
 * L'échange est-il permis ? Règle structurelle, puis habilitations dans les DEUX
 * sens. `detenues` vide (non chargées) : on ne bloque pas — « on ne sait pas »
 * n'est pas « non », et le serveur tranche.
 */
export function echangePossible(
  a: CaseEchange, b: CaseEchange,
  jour: LigneDuJour[],
  postes: Map<string, PosteEchange>,
  detenues: Map<string, Map<string, string>>,
  nomDe: (orgId: string) => string,
): { ok: boolean; motif?: string } {
  const refus = refusEchange(a, b, jour);
  if (refus) return { ok: false, motif: refus };
  const pa = postes.get(a.posteCode); const pb = postes.get(b.posteCode);
  if (!pa || !pb) return { ok: false, motif: 'Poste inconnu.' };
  if (pa.actif === false || pb.actif === false) return { ok: false, motif: 'L’un des deux postes est désactivé.' };
  if (detenues.size) {
    for (const [qui, poste] of [[a.orgId, pb], [b.orgId, pa]] as const) {
      const v = verdictLocal({ orgId: qui }, poste, detenues);
      if (!v.ok) {
        const manque = [...v.manquantes, ...v.enFormation.map((c) => `${c} (en formation)`)].join(', ');
        return { ok: false, motif: `${nomDe(qui)} n’est pas habilité(e) sur « ${poste.libelle || '?'} » — il manque : ${manque}.` };
      }
    }
  }
  return { ok: true };
}
